import React, { Fragment, useRef, useState } from 'react';
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import {
	ArrowLeftStartOnRectangleIcon,
	ChartBarSquareIcon,
	Cog6ToothIcon,
	FolderIcon,
	GlobeAltIcon,
	ServerIcon,
	SignalIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	Bars3Icon,
	ChevronRightIcon,
	ChevronUpDownIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import LogoMark from '../../images/logo-mark.png';
import { useNavigate } from 'react-router-dom';
import Avatar, { AvatarGenerated } from '../../components/Avatar';
import { getUserFullName } from '../../components/helpers/functions';
import useLocal from '../../hooks/useLocal';
import useModal from '../../hooks/useModal';

const teams = [
	{ id: 1, name: 'Planetaria', href: '#', initial: 'P', current: false },
	{ id: 2, name: 'Protocol', href: '#', initial: 'P', current: false },
	{ id: 3, name: 'Tailwind Labs', href: '#', initial: 'T', current: false },
];
const statuses = {
	offline: 'text-gray-500 bg-gray-100/10',
	online: 'text-green-400 bg-green-400/10',
	error: 'text-rose-400 bg-rose-400/10',
};
const environments = {
	Preview: 'text-gray-400 bg-gray-400/10 ring-gray-400/20',
	Production: 'text-indigo-400 bg-indigo-400/10 ring-indigo-400/30',
};
const deployments = [
	{
		id: 1,
		href: '#',
		projectName: 'ios-app',
		teamName: 'Planetaria',
		status: 'offline',
		statusText: 'Initiated 1m 32s ago',
		description: 'Deploys from GitHub',
		environment: 'Preview',
	},
	// More deployments...
];
const activityItems = [
	{
		user: {
			name: 'Michael Foster',
			imageUrl:
				'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
		},
		projectName: 'ios-app',
		commit: '2d89f0c8',
		branch: 'main',
		date: '1h',
		dateTime: '2023-01-23T11:00',
	},
	// More items...
];

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const BetaShell = ({ navigation, secondaryNavigation, children }) => {
	const navigate = useNavigate();
	const searchBar = useRef();
	const { setOpen: openGlobalSearch } = useModal('globalSearch');

	const { get } = useLocal();
	const user = get('user');

	const [sidebarOpen, setSidebarOpen] = useState(false);

	const onClickLink = (path) => {
		if (path) {
			navigate(path);
		}
		setSidebarOpen(false);
	};

	const topLevelPath = history?.location?.pathname?.split('/').filter(Boolean);
	const getCurrentPath = (level = 0) =>
		topLevelPath?.length ? topLevelPath[level] : '';

	const SIDEBAR = () => (
		<>
			{/* Sidebar component, swap this element with another sidebar if you like */}
			<div className="flex grow flex-col gap-y-5 overflow-y-auto bg-black/10 px-6 ring-1 ring-white/5">
				<div className="flex h-16 shrink-0 items-center">
					<img
						className="h-8 w-auto cursor-pointer"
						src={LogoMark}
						alt="Scipods"
						onClick={() => navigate('/')}
					/>
				</div>
				<nav className="flex flex-1 flex-col">
					<ul role="list" className="flex flex-1 flex-col gap-y-7">
						<li>
							<ul role="list" className="-mx-2 space-y-1">
								{navigation.map((item) => {
									const itemPath = item?.path?.split('/')?.filter(Boolean);
									const ITEM_PATH = itemPath?.length ? itemPath[0] : '';
									const current = ITEM_PATH === getCurrentPath(0);

									return (
										<li key={item.name}>
											<a
												onClick={() => onClickLink(item.path)}
												className={classNames(
													current
														? 'bg-gray-800 text-white'
														: 'text-gray-400 hover:bg-gray-800 hover:text-white',
													'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold cursor-pointer'
												)}
											>
												<item.icon
													aria-hidden="true"
													className="size-6 shrink-0"
												/>
												{item.name}
											</a>
										</li>
									);
								})}
							</ul>
						</li>
						<li>
							<div className="text-xs/6 font-semibold text-gray-400">
								Manage
							</div>
							<ul role="list" className="-mx-2 mt-2 space-y-1">
								{secondaryNavigation?.map((team) => {
									const itemPath = team?.path?.split('/')?.filter(Boolean);
									const ITEM_PATH = itemPath?.length ? itemPath[1] : '';
									const current = ITEM_PATH === getCurrentPath(1);

									return (
										<li key={team.name}>
											<a
												onClick={() => onClickLink(team.path)}
												className={classNames(
													current
														? 'bg-gray-800 text-white'
														: 'text-gray-400 hover:bg-gray-800 hover:text-white',
													'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold cursor-pointer'
												)}
											>
												<span className="flex size-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white">
													<team.icon
														aria-hidden="true"
														className="size-4 shrink-0"
													/>
												</span>
												<span className="truncate">{team.name}</span>
											</a>
										</li>
									);
								})}
							</ul>
						</li>

						<li className="mt-auto pb-4">
							<li>
								<ul role="list" className="-mx-2 mt-2 space-y-1">
									{[
										{
											path: '/settings',
											name: 'Settings',
											key: 'settings',
										},
										{
											path: '/logout',
											name: 'Logout',
											current: false,
										},
									]?.map((team) => {
										const itemPath = team?.path?.split('/')?.filter(Boolean);

										const ITEM_PATH = itemPath?.length ? itemPath[0] : '';

										return (
											<li key={team.name}>
												<a
													onClick={() => onClickLink(team.path)}
													className={classNames(
														ITEM_PATH === getCurrentPath(0)
															? 'bg-gray-800 text-white'
															: 'text-gray-400 hover:bg-gray-800 hover:text-white',
														'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold cursor-pointer'
													)}
												>
													<span className="truncate">{team.name}</span>
												</a>
											</li>
										);
									})}
								</ul>
							</li>
						</li>
					</ul>
				</nav>
			</div>
		</>
	);

	return (
		<>
			{/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-900">
        <body class="h-full">
        ```
      */}
			<div>
				<Transition.Root as={Fragment} show={sidebarOpen}>
					<Dialog
						open={sidebarOpen}
						onClose={setSidebarOpen}
						className="relative z-10 xl:hidden"
					>
						<Dialog.Backdrop
							transition
							className="fixed inset-0 bg-gray-50/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
						/>

						<div className="fixed inset-0 flex">
							<Dialog.Panel
								transition
								className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
							>
								<Transition.Child>
									<div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
										<button
											type="button"
											onClick={() => setSidebarOpen(false)}
											className="-m-2.5 p-2.5"
										>
											<span className="sr-only">Close sidebar</span>
											<XMarkIcon
												aria-hidden="true"
												className="size-6 text-white"
											/>
										</button>
									</div>
								</Transition.Child>
								{/* Sidebar component, swap this element with another sidebar if you like */}
								<div className="flex grow flex-col overflow-y-auto bg-gray-900 ring-1 ring-white/10">
									<SIDEBAR />
								</div>
							</Dialog.Panel>
						</div>
					</Dialog>
				</Transition.Root>

				{/* Static sidebar for desktop */}
				<div className="hidden xl:fixed xl:inset-y-0 xl:z-10 xl:flex xl:w-72 xl:flex-col bg-gray-900">
					<SIDEBAR />
				</div>

				<div className="xl:pl-72">
					{/* Sticky search header */}
					<div className="sticky top-0 z-10 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 px-4 shadow-sm sm:px-6 lg:px-8 bg-white">
						<button
							type="button"
							onClick={() => setSidebarOpen(true)}
							className="-m-2.5 p-2.5 text-white xl:hidden"
						>
							<span className="sr-only">Open sidebar</span>
							<Bars3Icon aria-hidden="true" className="size-5 text-gray-500" />
						</button>

						<div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
							<div className="flex flex-1">
								<label htmlFor="search-field" className="sr-only">
									Search
								</label>
								<div className="relative w-full text-gray-400 focus-within:text-gray-600">
									<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
										<MagnifyingGlassIcon
											className="h-5 w-5"
											aria-hidden="true"
										/>
									</div>
									<input
										ref={searchBar}
										id="search-field"
										className="block h-full w-full border-transparent py-2 pl-8 pr-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
										placeholder="Search"
										type="search"
										name="search"
										autoFocus={false}
										value={''}
										onFocus={(e) => {
											e.target.blur();
										}}
										onClick={() => openGlobalSearch()}
										readOnly
									/>
								</div>
							</div>
						</div>
					</div>

					<main className="flex flex-1 overflow-hidden h-full">{children}</main>

					{/* Activity feed 
                    
                    CHANGE <main>{children}</main> to <main className="lg:pr-96">*/}
					{/*<aside className="bg-black/10 lg:fixed lg:bottom-0 lg:right-0 lg:top-16 lg:w-96 lg:overflow-y-auto lg:border-l lg:border-white/5">
						<header className="flex items-center justify-between border-b border-white/5 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
							<h2 className="text-base/7 font-semibold text-white">
								Activity feed
							</h2>
							<a href="#" className="text-sm/6 font-semibold text-indigo-400">
								View all
							</a>
						</header>
						<ul role="list" className="divide-y divide-white/5">
							{activityItems.map((item) => (
								<li key={item.commit} className="px-4 py-4 sm:px-6 lg:px-8">
									<div className="flex items-center gap-x-3">
										<img
											alt=""
											src={item.user.imageUrl}
											className="size-6 flex-none rounded-full bg-gray-800"
										/>
										<h3 className="flex-auto truncate text-sm/6 font-semibold text-white">
											{item.user.name}
										</h3>
										<time
											dateTime={item.dateTime}
											className="flex-none text-xs text-gray-600"
										>
											{item.date}
										</time>
									</div>
									<p className="mt-3 truncate text-sm text-gray-500">
										Pushed to{' '}
										<span className="text-gray-400">{item.projectName}</span> (
										<span className="font-mono text-gray-400">
											{item.commit}
										</span>{' '}
										on <span className="text-gray-400">{item.branch}</span>)
									</p>
								</li>
							))}
                                        </ul>
					</aside>*/}
				</div>
			</div>
		</>
	);
};

export default BetaShell;
